import React, { useEffect, useState, useMemo } from "react";
import { Form, Col, Row, OverlayTrigger, Tooltip } from "react-bootstrap";
import { mapUserRoles } from "../../../functions/useRoleMapping";
import { useSelector, useDispatch } from "react-redux";
import useHandleBlur from "../../../functions/useHandleBlur";
import { useFormState } from "../../../functions/useFormState"; // Reusable form hook
import {
  selectUserDetails,
  selectUserRoles,
} from "../../../redux/slices/userSlice";
import { selectApiUrls } from "../../../redux/slices/apiUrlSlice";
import { setSoldToShipToDetails } from "../../../redux/slices/psnSlice";
import { setJobDetail } from "../../../redux/slices/jobDetailsSlice";
import { selectSoldToShipTo, selectFieldDescriptions } from "../../../redux/selectors";
import useFieldClass from "../../../functions/useFieldClass";

function SoldToShipTo({ zone }) {
  const dispatch = useDispatch();
  const userDetails = useSelector(selectUserDetails); // User details
  const userRoles = useSelector(selectUserRoles); // User details
  const currentUser = userDetails?.name;
  const sectionName = "soldToShipTo";

  const apiUrls = useSelector(selectApiUrls); // Fetch API URLs from Redux
  const apiUrl = apiUrls[sectionName];
  const tableKey = sectionName;

  const { options } = useSelector((state) => state.dropdown); // Fetch dropdown values from redux

  const storedJobDetails = useSelector(selectSoldToShipTo);
  const activetab = useSelector(
    (state) => state.activeMechanicalTab.selectedZone
  );
  const jobDetails = useMemo(
    () => storedJobDetails.find((detail) => detail.Zone === activetab) || {},
    [storedJobDetails, activetab]
  );

  const error = null;
  const loading = storedJobDetails.length === 0;

  const { formData, handleChange, setFormData } = useFormState({}); // Form state management

  useEffect(() => {
    if (jobDetails) {
      dispatch(setSoldToShipToDetails(jobDetails));
      setFormData({
        ID: jobDetails.ID || "",
        certificationOfOriginRequired:
          jobDetails.CertificationOfOriginRequired || "",
        containerTerms: jobDetails.ContainerTerms || "",
        hSCode: jobDetails.HSCode || "",
        incoTerms: jobDetails.IncoTerms || "",
        letterOfCredit: jobDetails.LetterOfCredit || "",
        splitLoad: jobDetails.SplitLoad || "",
        customerToBroker: jobDetails.CustomerToBroker || "",
        manifestRequired: jobDetails.ManifestRequired || "",
        shippersLetterOfIntentRequired:
          jobDetails.ShippersLetterOfIntentRequired || "",
        insuranceCertificateRequired:
          jobDetails.InsuranceCertificateRequired || "",
        pointOfDestination: jobDetails.PointOfDestination || "",
        watertronicsCoversInsurance:
          jobDetails.WatertronicsCoversInsurance || "",
        customerCoordinatedBrokerFirstName:
          jobDetails.CustomerCoordinatedBrokerFirstName || "",
        customerCoordinatedBrokerLastName:
          jobDetails.CustomerCoordinatedBrokerLastName || "",
        customerCoordinatedBrokerCompany:
          jobDetails.CustomerCoordinatedBrokerCompany || "",
        customerCoordinatedBrokerEmail:
          jobDetails.CustomerCoordinatedBrokerEmail || "",
        customerCoordinatedBrokerPhone:
          jobDetails.CustomerCoordinatedBrokerPhone || "",
        commercialInvoiceStampOrSeal:
          jobDetails.CommercialInvoiceStampOrSeal || "",
        commercialInvoiceCustomerPOReferenceRequired:
          jobDetails.CommercialInvoiceCustomerPOReferenceRequired || "",
        internationalShipmentComments:
          jobDetails.InternationalShipmentComments || "",
      });
    }
  }, [dispatch, jobDetails, setFormData]);

  useEffect(() => {
    if (formData && formData.ID) {
      const { internationalShip } = formData;
      dispatch(setJobDetail({ internationalShip }));
    }
  }, [formData, dispatch]);

  const jobCheckoutStatus = useSelector((state) => state.checkout.job);
  const isJobCheckedOut = jobCheckoutStatus?.IsCheckedOut === true;
  const isCurrentUserCheckedOut =
    jobCheckoutStatus?.CheckedOutBy === currentUser;
  const readOnly = useSelector((state) => state.readOnly.isReadOnly); // Fetch read-only state from Redux
  const isDisabled = (isJobCheckedOut && !isCurrentUserCheckedOut) || readOnly; // Update isDisabled condition

  // State to store role variables
  const [roles, setRoles] = useState({
    Admin: false,
    Apps: false,
    Engineer: false,
    TestPit: false,
    PSN: false,
    Sales: false,
    ReadOnly: false,
  });

  const { handleFocus, handleBlur, showNotification, saveStatus } =
    useHandleBlur(formData);

  useEffect(() => {
    setRoles(mapUserRoles(userRoles));
  }, [userRoles]);

  const fieldClasses = useFieldClass(formData, zone); // Use useFieldClass hook

  const fieldDescriptions = useSelector(selectFieldDescriptions);

  const getFieldTitle = (fieldName) => {
    return (
      fieldDescriptions.find((desc) => desc.ColumnName === fieldName)
        ?.Description || ""
    );
  };

  const renderTooltip = (fieldName) => (
    <Tooltip id={`tooltip-${fieldName}`}>
      {getFieldTitle(fieldName)
        .split("\n")
        .map((line, index) => (
          <span key={index}>
            {line}
            <br />
          </span>
        ))}
    </Tooltip>
  );

  /****************Add dropdown options here*************************/

  const genericOptions =
    options
      ?.filter((option) => option.DropdownType === "Generic Yes/ No")
      .sort((a, b) => a.SortOrder - b.SortOrder) || [];
  const incoTermsOptions =
    options
      ?.filter((option) => option.DropdownType === "IncoTerms")
      .sort((a, b) => a.SortOrder - b.SortOrder) || [];
  const htscodeOptions =
    options
      ?.filter((option) => option.DropdownType === "HTS Code")
      .sort((a, b) => a.SortOrder - b.SortOrder) || [];
  const containerTermsOptions =
    options
      ?.filter((option) => option.DropdownType === "Container Terms")
      .sort((a, b) => a.SortOrder - b.SortOrder) || [];

  /******************************************************************/

  const handleSubmit = (e) => {
    e.preventDefault();
  };

  const isValueInDropdown = (value, dropdownOptions) => {
    const exists = dropdownOptions.some(
      (option) => option.DisplayText === value || option.displayName === value
    );
    return exists;
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    ); // Show loading spinner
  }

  if (error) {
    return <div className="error">{error}</div>; // Show error message if fetch fails
  }

  return (
    <div>
      {error ? (
        <p style={{ color: "red" }}>Error: {error}</p>
      ) : !formData ? (
        <p>Loading...</p>
      ) : (
        <div>
          <div>
            {showNotification && (
              <div
                className={`save-notification ${
                  saveStatus.includes("Failed") ? "error" : ""
                }`}
              >
                {saveStatus}
              </div>
            )}
          </div>
          <Row className="international-shipping-section">
            <Row className="international-shipping-header">
              <Col className="mb-3">
                <h3 className="subsection-header">
                  International Shipment Details
                </h3>
                <p className="text-center">Watertronics Tax ID# 39-1794482</p>
              </Col>
            </Row>

            <Form
              onSubmit={handleSubmit}
              className="international-shipping-form container mt-4"
            >
              <Row className="international-shipping-grid mb-3">
                <Col className="international-shipping-panel">
                  {/****************************************Column 1 */}
                  <Col className="mb-3">
                    <Form.Group as={Row} controlId="incoTerms">
                      <Form.Label column="md" md={3}>
                        <OverlayTrigger
                          placement="top"
                          overlay={renderTooltip("IncoTerms")}
                          delay={{ show: 300, hide: 200 }}
                        >
                          <span>IncoTerms</span>
                        </OverlayTrigger>
                      </Form.Label>
                      <Col md={9}>
                        <Form.Select
                          name="incoTerms"
                          value={formData.incoTerms}
                          onFocus={handleFocus}
                          onBlur={(e) =>
                            handleBlur(e, "IncoTerms", genericOptions, {
                              ID: formData.ID,
                              url: apiUrl,
                              field: "IncoTerms",
                              tableKey,
                            })
                          }
                          disabled={
                            isDisabled ||
                            (!roles.Admin && !roles.Apps && !roles.PSN)
                          }
                          className={fieldClasses.incoTerms}
                          onChange={handleChange}
                        >
                          <option value="">Select IncoTerms</option>
                          {incoTermsOptions.map((option) => (
                            <option key={option.ID} value={option.DisplayText}>
                              {option.DisplayText}
                            </option>
                          ))}
                          {!isValueInDropdown(
                            formData.incoTerms,
                            incoTermsOptions
                          ) &&
                            formData.incoTerms && (
                              <option
                                value={formData.incoTerms}
                                style={{ color: "red" }}
                              >
                                *{formData.incoTerms}
                              </option>
                            )}
                        </Form.Select>
                      </Col>
                    </Form.Group>
                  </Col>
                  <Col className="mb-3">
                    <Form.Group as={Row} controlId="letterOfCredit">
                      <Form.Label column="md" md={3}>
                        <OverlayTrigger
                          placement="top"
                          overlay={renderTooltip("LetterOfCredit")}
                          delay={{ show: 300, hide: 200 }}
                        >
                          <span>Letter Of Credit</span>
                        </OverlayTrigger>
                      </Form.Label>
                      <Col md={9}>
                        <Form.Select
                          name="letterOfCredit"
                          value={formData.letterOfCredit}
                          onFocus={handleFocus}
                          onBlur={(e) =>
                            handleBlur(e, "Letter Of Credit", genericOptions, {
                              ID: formData.ID,
                              url: apiUrl,
                              field: "LetterOfCredit",
                              tableKey,
                            })
                          }
                          disabled={
                            isDisabled ||
                            (!roles.Admin && !roles.Apps && !roles.PSN)
                          }
                          className={fieldClasses.letterOfCredit}
                          onChange={handleChange}
                        >
                          <option value="">Select Letter Of Credit</option>
                          {genericOptions.map((option) => (
                            <option key={option.ID} value={option.DisplayText}>
                              {option.DisplayText}
                            </option>
                          ))}
                          {!isValueInDropdown(
                            formData.letterOfCredit,
                            genericOptions
                          ) &&
                            formData.letterOfCredit && (
                              <option
                                value={formData.letterOfCredit}
                                style={{ color: "red" }}
                              >
                                *{formData.letterOfCredit}
                              </option>
                            )}
                        </Form.Select>
                      </Col>
                    </Form.Group>
                  </Col>
                  <Col className="mb-3">
                    <Form.Group as={Row} controlId="splitLoad">
                      <Form.Label column="md" md={3}>
                        <OverlayTrigger
                          placement="top"
                          overlay={renderTooltip("SplitLoad")}
                          delay={{ show: 300, hide: 200 }}
                        >
                          <span>Split Load</span>
                        </OverlayTrigger>
                      </Form.Label>
                      <Col md={9}>
                        <Form.Select
                          name="splitLoad"
                          value={formData.splitLoad}
                          onFocus={handleFocus}
                          onBlur={(e) =>
                            handleBlur(e, "Split Load", genericOptions, {
                              ID: formData.ID,
                              url: apiUrl,
                              field: "SplitLoad",
                              tableKey,
                            })
                          }
                          disabled={
                            isDisabled ||
                            (!roles.Admin && !roles.Apps && !roles.PSN)
                          }
                          className={fieldClasses.splitLoad}
                          onChange={handleChange}
                        >
                          <option value="">Select Split Load</option>
                          {genericOptions.map((option) => (
                            <option key={option.ID} value={option.DisplayText}>
                              {option.DisplayText}
                            </option>
                          ))}
                          {!isValueInDropdown(
                            formData.splitLoad,
                            genericOptions
                          ) &&
                            formData.splitLoad && (
                              <option
                                value={formData.splitLoad}
                                style={{ color: "red" }}
                              >
                                *{formData.splitLoad}
                              </option>
                            )}
                        </Form.Select>
                      </Col>
                    </Form.Group>
                  </Col>
                  <Col className="mb-3">
                    <Form.Group as={Row} controlId="containerTerms">
                      <Form.Label column="md" md={3}>
                        <OverlayTrigger
                          placement="top"
                          overlay={renderTooltip("ContainerTerms")}
                          delay={{ show: 300, hide: 200 }}
                        >
                          <span>Container Terms</span>
                        </OverlayTrigger>
                      </Form.Label>
                      <Col md={9}>
                        <Form.Select
                          name="containerTerms"
                          value={formData.containerTerms}
                          onFocus={handleFocus}
                          onBlur={(e) =>
                            handleBlur(e, "Container Terms", genericOptions, {
                              ID: formData.ID,
                              url: apiUrl,
                              field: "ContainerTerms",
                              tableKey,
                            })
                          }
                          disabled={
                            isDisabled ||
                            (!roles.Admin && !roles.Apps && !roles.PSN)
                          }
                          className={fieldClasses.containerTerms}
                          onChange={handleChange}
                        >
                          <option value="">Select Container Terms</option>
                          {containerTermsOptions.map((option) => (
                            <option key={option.ID} value={option.DisplayText}>
                              {option.DisplayText}
                            </option>
                          ))}
                          {!isValueInDropdown(
                            formData.containerTerms,
                            containerTermsOptions
                          ) &&
                            formData.containerTerms && (
                              <option
                                value={formData.containerTerms}
                                style={{ color: "red" }}
                              >
                                *{formData.containerTerms}
                              </option>
                            )}
                        </Form.Select>
                      </Col>
                    </Form.Group>
                  </Col>
                  <Col className="mb-3">
                    <Form.Group as={Row} controlId="hSCode">
                      <Form.Label column="md" md={3}>
                        <OverlayTrigger
                          placement="top"
                          overlay={renderTooltip("HSCode")}
                          delay={{ show: 300, hide: 200 }}
                        >
                          <span>HTS Code</span>
                        </OverlayTrigger>
                      </Form.Label>
                      <Col md={9}>
                        <Form.Select
                          name="hSCode"
                          value={formData.hSCode}
                          onFocus={handleFocus}
                          onBlur={(e) =>
                            handleBlur(e, "HS Code", genericOptions, {
                              ID: formData.ID,
                              url: apiUrl,
                              field: "HSCode",
                              tableKey,
                            })
                          }
                          disabled={
                            isDisabled ||
                            (!roles.Admin && !roles.Apps && !roles.PSN)
                          }
                          className={fieldClasses.hSCode}
                          onChange={handleChange}
                        >
                          <option value="">Select HTS Code</option>
                          {htscodeOptions.map((option) => (
                            <option key={option.ID} value={option.DisplayText}>
                              {option.DisplayText}
                            </option>
                          ))}
                          {!isValueInDropdown(
                            formData.hSCode,
                            htscodeOptions
                          ) &&
                            formData.hSCode && (
                              <option
                                value={formData.hSCode}
                                style={{ color: "red" }}
                              >
                                *{formData.hSCode}
                              </option>
                            )}
                        </Form.Select>
                      </Col>
                    </Form.Group>
                  </Col>
                  <Col className="mb-3">
                    <Form.Group as={Row} controlId="customerToBroker">
                      <Form.Label column="md" md={3}>
                        <OverlayTrigger
                          placement="top"
                          overlay={renderTooltip("CustomerToBroker")}
                          delay={{ show: 300, hide: 200 }}
                        >
                          <span>Customer to Broker</span>
                        </OverlayTrigger>
                      </Form.Label>
                      <Col md={9}>
                        <Form.Select
                          name="customerToBroker"
                          value={formData.customerToBroker}
                          onFocus={handleFocus}
                          onBlur={(e) =>
                            handleBlur(
                              e,
                              "Customer to Broker",
                              genericOptions,
                              {
                                ID: formData.ID,
                                url: apiUrl,
                                field: "CustomerToBroker",
                                tableKey,
                              }
                            )
                          }
                          disabled={
                            isDisabled ||
                            (!roles.Admin && !roles.Apps && !roles.PSN)
                          }
                          className={fieldClasses.customerToBroker}
                          onChange={handleChange}
                        >
                          <option value="">Select Customer to Broker</option>
                          {genericOptions.map((option) => (
                            <option key={option.ID} value={option.DisplayText}>
                              {option.DisplayText}
                            </option>
                          ))}
                          {!isValueInDropdown(
                            formData.customerToBroker,
                            genericOptions
                          ) &&
                            formData.customerToBroker && (
                              <option
                                value={formData.customerToBroker}
                                style={{ color: "red" }}
                              >
                                *{formData.customerToBroker}
                              </option>
                            )}
                        </Form.Select>
                      </Col>
                    </Form.Group>
                  </Col>
                  <Col className="mb-3">
                    <Form.Group as={Row} controlId="manifestRequired">
                      <Form.Label column="md" md={3}>
                        <OverlayTrigger
                          placement="top"
                          overlay={renderTooltip("ManifestRequired")}
                          delay={{ show: 300, hide: 200 }}
                        >
                          <span>Manifest Required</span>
                        </OverlayTrigger>
                      </Form.Label>
                      <Col md={9}>
                        <Form.Select
                          name="manifestRequired"
                          value={formData.manifestRequired}
                          onFocus={handleFocus}
                          onBlur={(e) =>
                            handleBlur(e, "Manifest Required", genericOptions, {
                              ID: formData.ID,
                              url: apiUrl,
                              field: "ManifestRequired",
                              tableKey,
                            })
                          }
                          disabled={
                            isDisabled ||
                            (!roles.Admin && !roles.Apps && !roles.PSN)
                          }
                          className={fieldClasses.manifestRequired}
                          onChange={handleChange}
                        >
                          <option value="">Select Manifest Required</option>
                          {genericOptions.map((option) => (
                            <option key={option.ID} value={option.DisplayText}>
                              {option.DisplayText}
                            </option>
                          ))}
                          {!isValueInDropdown(
                            formData.manifestRequired,
                            genericOptions
                          ) &&
                            formData.manifestRequired && (
                              <option
                                value={formData.manifestRequired}
                                style={{ color: "red" }}
                              >
                                *{formData.manifestRequired}
                              </option>
                            )}
                        </Form.Select>
                      </Col>
                    </Form.Group>
                  </Col>
                  <Col className="mb-3">
                    <Form.Group
                      as={Row}
                      controlId="shippersLetterOfIntentRequired"
                    >
                      <Form.Label column="md" md={3}>
                        <OverlayTrigger
                          placement="top"
                          overlay={renderTooltip(
                            "ShippersLetterOfIntentRequired"
                          )}
                          delay={{ show: 300, hide: 200 }}
                        >
                          <span>Shippers Letter of Intent Required</span>
                        </OverlayTrigger>
                      </Form.Label>
                      <Col md={9}>
                        <Form.Select
                          name="shippersLetterOfIntentRequired"
                          value={formData.shippersLetterOfIntentRequired}
                          onFocus={handleFocus}
                          onBlur={(e) =>
                            handleBlur(
                              e,
                              "Shippers Letter of Intent Required",
                              genericOptions,
                              {
                                ID: formData.ID,
                                url: apiUrl,
                                field: "ShippersLetterOfIntentRequired",
                                tableKey,
                              }
                            )
                          }
                          disabled={
                            isDisabled ||
                            (!roles.Admin && !roles.Apps && !roles.PSN)
                          }
                          className={fieldClasses.shippersLetterOfIntentRequired}
                          onChange={handleChange}
                        >
                          <option value="">
                            Select Shippers Letter of Intent Required
                          </option>
                          {genericOptions.map((option) => (
                            <option key={option.ID} value={option.DisplayText}>
                              {option.DisplayText}
                            </option>
                          ))}
                          {!isValueInDropdown(
                            formData.shippersLetterOfIntentRequired,
                            genericOptions
                          ) &&
                            formData.shippersLetterOfIntentRequired && (
                              <option
                                value={formData.shippersLetterOfIntentRequired}
                                style={{ color: "red" }}
                              >
                                *{formData.shippersLetterOfIntentRequired}
                              </option>
                            )}
                        </Form.Select>
                      </Col>
                    </Form.Group>
                  </Col>
                  <Col className="mb-3">
                    <Form.Group
                      as={Row}
                      controlId="insuranceCertificateRequired"
                    >
                      <Form.Label column="md" md={3}>
                        <OverlayTrigger
                          placement="top"
                          overlay={renderTooltip(
                            "InsuranceCertificateRequired"
                          )}
                          delay={{ show: 300, hide: 200 }}
                        >
                          <span>Insurance Certificate Required</span>
                        </OverlayTrigger>
                      </Form.Label>
                      <Col md={9}>
                        <Form.Select
                          name="insuranceCertificateRequired"
                          value={formData.insuranceCertificateRequired}
                          onFocus={handleFocus}
                          onBlur={(e) =>
                            handleBlur(
                              e,
                              "Insurance Certificate Required",
                              genericOptions,
                              {
                                ID: formData.ID,
                                url: apiUrl,
                                field: "InsuranceCertificateRequired",
                                tableKey,
                              }
                            )
                          }
                          disabled={
                            isDisabled ||
                            (!roles.Admin && !roles.Apps && !roles.PSN)
                          }
                          className={fieldClasses.insuranceCertificateRequired}
                          onChange={handleChange}
                        >
                          <option value="">
                            Select Insurance Certificate Required
                          </option>
                          {genericOptions.map((option) => (
                            <option key={option.ID} value={option.DisplayText}>
                              {option.DisplayText}
                            </option>
                          ))}
                          {!isValueInDropdown(
                            formData.insuranceCertificateRequired,
                            genericOptions
                          ) &&
                            formData.insuranceCertificateRequired && (
                              <option
                                value={formData.insuranceCertificateRequired}
                                style={{ color: "red" }}
                              >
                                *{formData.insuranceCertificateRequired}
                              </option>
                            )}
                        </Form.Select>
                      </Col>
                    </Form.Group>
                  </Col>
                  <Col className="mb-3">
                    <Form.Group
                      as={Row}
                      controlId="certificateOfOriginRequired"
                    >
                      <Form.Label column="md" md={3}>
                        <OverlayTrigger
                          placement="top"
                          overlay={renderTooltip(
                            "CertificateOfOriginRequired"
                          )}
                          delay={{ show: 300, hide: 200 }}
                        >
                          <span>Certificate Of Origin Required</span>
                        </OverlayTrigger>
                      </Form.Label>
                      <Col md={9}>
                        <Form.Select
                          name="certificateOfOriginRequired"
                          value={formData.certificateOfOriginRequired}
                          onFocus={handleFocus}
                          onBlur={(e) =>
                            handleBlur(
                              e,
                              "Certificate Of Origin Required",
                              genericOptions,
                              {
                                ID: formData.ID,
                                url: apiUrl,
                                field: "CertificateOfOriginRequired",
                                tableKey,
                              }
                            )
                          }
                          disabled={
                            isDisabled ||
                            (!roles.Admin && !roles.Apps && !roles.PSN)
                          }
                          className={fieldClasses.certificateOfOriginRequired}
                          onChange={handleChange}
                        >
                          <option value="">
                            Select Certificate Of Origin Required
                          </option>
                          {genericOptions.map((option) => (
                            <option key={option.ID} value={option.DisplayText}>
                              {option.DisplayText}
                            </option>
                          ))}
                          {!isValueInDropdown(
                            formData.certificateOfOriginRequired,
                            genericOptions
                          ) &&
                            formData.certificateOfOriginRequired && (
                              <option
                                value={formData.certificateOfOriginRequired}
                                style={{ color: "red" }}
                              >
                                *{formData.certificateOfOriginRequired}
                              </option>
                            )}
                        </Form.Select>
                      </Col>
                    </Form.Group>
                  </Col>
                  <Col className="mb-3">
                    <Form.Group as={Row} controlId="pointOfDestination">
                      <Form.Label column="md" md={3}>
                        <OverlayTrigger
                          placement="top"
                          overlay={renderTooltip("PointOfDestination")}
                          delay={{ show: 300, hide: 200 }}
                        >
                          <span>Point Of Destination</span>
                        </OverlayTrigger>
                      </Form.Label>
                      <Col md={9}>
                        <Form.Control
                          type="text"
                          name="pointOfDestination"
                          value={formData.pointOfDestination}
                          onFocus={handleFocus}
                          onBlur={(e) =>
                            handleBlur(e, "Point Of Destination", [], {
                              ID: formData.ID,
                              url: apiUrl,
                              field: "PointOfDestination",
                              tableKey,
                            })
                          }
                          disabled={
                            isDisabled ||
                            (!roles.Admin && !roles.Apps && !roles.PSN)
                          }
                          className={fieldClasses.pointOfDestination}
                          onChange={handleChange}
                          placeholder="Enter Point Of Destination"
                        />
                      </Col>
                    </Form.Group>
                  </Col>

                  <Col className="mb-3">
                    <Form.Group
                      as={Row}
                      controlId="watertronicsCoversInsurance"
                    >
                      <Form.Label column="md" md={3}>
                        <OverlayTrigger
                          placement="top"
                          overlay={renderTooltip(
                            "WatertronicsCoversInsurance"
                          )}
                          delay={{ show: 300, hide: 200 }}
                        >
                          <span>Watertronics Covers Insurance</span>
                        </OverlayTrigger>
                      </Form.Label>
                      <Col md={9}>
                        <Form.Select
                          name="watertronicsCoversInsurance"
                          value={formData.watertronicsCoversInsurance}
                          onFocus={handleFocus}
                          onBlur={(e) =>
                            handleBlur(
                              e,
                              "Watertronics Covers Insurance",
                              genericOptions,
                              {
                                ID: formData.ID,
                                url: apiUrl,
                                field: "WatertronicsCoversInsurance",
                                tableKey,
                              }
                            )
                          }
                          disabled={
                            isDisabled ||
                            (!roles.Admin && !roles.Apps && !roles.PSN)
                          }
                          className={fieldClasses.watertronicsCoversInsurance}
                          onChange={handleChange}
                        >
                          <option value="">
                            Select Watertronics Covers Insurance
                          </option>
                          {genericOptions.map((option) => (
                            <option key={option.ID} value={option.DisplayText}>
                              {option.DisplayText}
                            </option>
                          ))}
                          {!isValueInDropdown(
                            formData.watertronicsCoversInsurance,
                            genericOptions
                          ) &&
                            formData.watertronicsCoversInsurance && (
                              <option
                                value={formData.watertronicsCoversInsurance}
                                style={{ color: "red" }}
                              >
                                *{formData.watertronicsCoversInsurance}
                              </option>
                            )}
                        </Form.Select>
                      </Col>
                    </Form.Group>
                  </Col>
                </Col>

                <Col className="international-shipping-panel">
                  {/************************************************** Column 2*/}
                  
                  <h3 className="subsection-header">
                    Customer Coordinated Broker
                  </h3>
                  <Col className="mb-3">
                    <Form.Group
                      as={Row}
                      controlId="customerCoordinatedBrokerCompany"
                    >
                      <Form.Label column="md" md={3}>
                        <OverlayTrigger
                          placement="top"
                          overlay={renderTooltip(
                            "CustomerCoordinatedBrokerCompany"
                          )}
                          delay={{ show: 300, hide: 200 }}
                        >
                          <span>Company</span>
                        </OverlayTrigger>
                      </Form.Label>
                      <Col md={9}>
                        <Form.Control
                          type="text"
                          name="customerCoordinatedBrokerCompany"
                          value={formData.customerCoordinatedBrokerCompany}
                          onFocus={handleFocus}
                          onBlur={(e) =>
                            handleBlur(
                              e,
                              "Customer Coordinated Broker Company",
                              [],
                              {
                                ID: formData.ID,
                                url: apiUrl,
                                field: "CustomerCoordinatedBrokerCompany",
                                tableKey,
                              }
                            )
                          }
                          disabled={
                            isDisabled ||
                            (!roles.Admin && !roles.Apps && !roles.PSN)
                          }
                          className={fieldClasses.customerCoordinatedBrokerCompany}
                          onChange={handleChange}
                          placeholder="Enter Company Name"
                        />
                      </Col>
                    </Form.Group>
                  </Col>
                  <Row className="mb-3"><Col className="mb-3">
                    <Form.Group
                      as={Row}
                      controlId="customerCoordinatedBrokerFirstName"
                    >
                      <Form.Label column="md" md={3}>
                        <OverlayTrigger
                          placement="top"
                          overlay={renderTooltip(
                            "CustomerCoordinatedBrokerFirstName"
                          )}
                          delay={{ show: 300, hide: 200 }}
                        >
                          <span>First Name</span>
                        </OverlayTrigger>
                      </Form.Label>
                      <Col md={9}>
                        <Form.Control
                          type="text"
                          name="customerCoordinatedBrokerFirstName"
                          value={formData.customerCoordinatedBrokerFirstName}
                          onFocus={handleFocus}
                          onBlur={(e) =>
                            handleBlur(
                              e,
                              "Customer Coordinated Broker First Name",
                              [],
                              {
                                ID: formData.ID,
                                url: apiUrl,
                                field: "CustomerCoordinatedBrokerFirstName",
                                tableKey,
                              }
                            )
                          }
                          disabled={
                            isDisabled ||
                            (!roles.Admin && !roles.Apps && !roles.PSN)
                          }
                          className={fieldClasses.customerCoordinatedBrokerFirstName}
                          onChange={handleChange}
                          placeholder="Enter First Name"
                        />
                      </Col>
                    </Form.Group>
                  </Col>
                  <Col className="mb-3">
                    <Form.Group
                      as={Row}
                      controlId="customerCoordinatedBrokerLastName"
                    >
                      <Form.Label column="md" md={3}>
                        <OverlayTrigger
                          placement="top"
                          overlay={renderTooltip(
                            "CustomerCoordinatedBrokerLastName"
                          )}
                          delay={{ show: 300, hide: 200 }}
                        >
                          <span>Last Name</span>
                        </OverlayTrigger>
                      </Form.Label>
                      <Col md={9}>
                        <Form.Control
                          type="text"
                          name="customerCoordinatedBrokerLastName"
                          value={formData.customerCoordinatedBrokerLastName}
                          onFocus={handleFocus}
                          onBlur={(e) =>
                            handleBlur(
                              e,
                              "Customer Coordinated Broker Last Name",
                              [],
                              {
                                ID: formData.ID,
                                url: apiUrl,
                                field: "CustomerCoordinatedBrokerLastName",
                                tableKey,
                              }
                            )
                          }
                          disabled={
                            isDisabled ||
                            (!roles.Admin && !roles.Apps && !roles.PSN)
                          }
                          className={fieldClasses.customerCoordinatedBrokerLastName}
                          onChange={handleChange}
                          placeholder="Enter Last Name"
                        />
                      </Col>
                    </Form.Group>
                  </Col></Row>
                  
                 <Row className="mb-3"> <Col className="mb-3">
                    <Form.Group
                      as={Row}
                      controlId="customerCoordinatedBrokerEmail"
                    >
                      <Form.Label column="md" md={3}>
                        <OverlayTrigger
                          placement="top"
                          overlay={renderTooltip(
                            "CustomerCoordinatedBrokerEmail"
                          )}
                          delay={{ show: 300, hide: 200 }}
                        >
                          <span>Email</span>
                        </OverlayTrigger>
                      </Form.Label>
                      <Col md={9}>
                        <Form.Control
                          type="email"
                          name="customerCoordinatedBrokerEmail"
                          value={formData.customerCoordinatedBrokerEmail}
                          onFocus={handleFocus}
                          onBlur={(e) =>
                            handleBlur(
                              e,
                              "Customer Coordinated Broker Email",
                              [],
                              {
                                ID: formData.ID,
                                url: apiUrl,
                                field: "CustomerCoordinatedBrokerEmail",
                                tableKey,
                              }
                            )
                          }
                          disabled={
                            isDisabled ||
                            (!roles.Admin && !roles.Apps && !roles.PSN)
                          }
                          className={fieldClasses.customerCoordinatedBrokerEmail}
                          onChange={handleChange}
                          placeholder="Enter Email"
                        />
                      </Col>
                    </Form.Group>
                  </Col>

                  <Col className="mb-3">
                    <Form.Group
                      as={Row}
                      controlId="customerCoordinatedBrokerPhone"
                    >
                      <Form.Label column="md" md={3}>
                        <OverlayTrigger
                          placement="top"
                          overlay={renderTooltip(
                            "CustomerCoordinatedBrokerPhone"
                          )}
                          delay={{ show: 300, hide: 200 }}
                        >
                          <span>Phone</span>
                        </OverlayTrigger>
                      </Form.Label>
                      <Col md={9}>
                        <Form.Control
                          type="text"
                          name="customerCoordinatedBrokerPhone"
                          value={formData.customerCoordinatedBrokerPhone}
                          onFocus={handleFocus}
                          onBlur={(e) =>
                            handleBlur(
                              e,
                              "Customer Coordinated Broker Phone",
                              [],
                              {
                                ID: formData.ID,
                                url: apiUrl,
                                field: "CustomerCoordinatedBrokerPhone",
                                tableKey,
                              }
                            )
                          }
                          disabled={
                            isDisabled ||
                            (!roles.Admin && !roles.Apps && !roles.PSN)
                          }
                          className={fieldClasses.customerCoordinatedBrokerPhone}
                          onChange={handleChange}
                          placeholder="Enter Phone Number"
                        />
                      </Col>
                    </Form.Group>
                  </Col></Row>
                 
                  <h3 className="subsection-header">
                    Commercial Invoice Details
                  </h3>
                  <Col className="mb-3">
                    <Form.Group
                      as={Row}
                      controlId="commercialInvoiceCustomerPOReferenceRequired"
                    >
                      <Form.Label column="md" md={3}>
                        <OverlayTrigger
                          placement="top"
                          overlay={renderTooltip(
                            "CommercialInvoiceCustomerPOReferenceRequired"
                          )}
                          delay={{ show: 300, hide: 200 }}
                        >
                          <span>Customer PO Reference Required</span>
                        </OverlayTrigger>
                      </Form.Label>
                      <Col md={9}>
                        <Form.Select
                          name="commercialInvoiceCustomerPOReferenceRequired"
                          value={
                            formData.commercialInvoiceCustomerPOReferenceRequired
                          }
                          onFocus={handleFocus}
                          onBlur={(e) =>
                            handleBlur(
                              e,
                              "Commercial Invoice Customer PO Reference Required",
                              genericOptions,
                              {
                                ID: formData.ID,
                                url: apiUrl,
                                field:
                                  "CommercialInvoiceCustomerPOReferenceRequired",
                                tableKey,
                              }
                            )
                          }
                          disabled={
                            isDisabled ||
                            (!roles.Admin && !roles.Apps && !roles.PSN)
                          }
                          className={fieldClasses.commercialInvoiceCustomerPOReferenceRequired}
                          onChange={handleChange}
                        >
                          <option value="">Select Yes/No</option>
                          {genericOptions.map((option) => (
                            <option key={option.ID} value={option.DisplayText}>
                              {option.DisplayText}
                            </option>
                          ))}
                          {!isValueInDropdown(
                            formData.commercialInvoiceCustomerPOReferenceRequired,
                            genericOptions
                          ) &&
                            formData.commercialInvoiceCustomerPOReferenceRequired && (
                              <option
                                value={
                                  formData.commercialInvoiceCustomerPOReferenceRequired
                                }
                                style={{ color: "red" }}
                              >
                                *
                                {
                                  formData.commercialInvoiceCustomerPOReferenceRequired
                                }
                              </option>
                            )}
                        </Form.Select>
                      </Col>
                    </Form.Group>
                  </Col>

                  <Col className="mb-3">
                    <Form.Group
                      as={Row}
                      controlId="commercialInvoiceStampOrSeal"
                    >
                      <Form.Label column="md" md={3}>
                        <OverlayTrigger
                          placement="top"
                          overlay={renderTooltip(
                            "CommercialInvoiceStampOrSeal"
                          )}
                          delay={{ show: 300, hide: 200 }}
                        >
                          <span>Stamp or Seal</span>
                        </OverlayTrigger>
                      </Form.Label>
                      <Col md={9}>
                        <Form.Select
                          name="commercialInvoiceStampOrSeal"
                          value={formData.commercialInvoiceStampOrSeal}
                          onFocus={handleFocus}
                          onBlur={(e) =>
                            handleBlur(
                              e,
                              "Commercial Invoice Stamp or Seal",
                              genericOptions,
                              {
                                ID: formData.ID,
                                url: apiUrl,
                                field: "CommercialInvoiceStampOrSeal",
                                tableKey,
                              }
                            )
                          }
                          disabled={
                            isDisabled ||
                            (!roles.Admin && !roles.Apps && !roles.PSN)
                          }
                          className={fieldClasses.commercialInvoiceStampOrSeal}
                          onChange={handleChange}
                        >
                          <option value="">Select Yes/No</option>
                          {genericOptions.map((option) => (
                            <option key={option.ID} value={option.DisplayText}>
                              {option.DisplayText}
                            </option>
                          ))}
                          {!isValueInDropdown(
                            formData.commercialInvoiceStampOrSeal,
                            genericOptions
                          ) &&
                            formData.commercialInvoiceStampOrSeal && (
                              <option
                                value={formData.commercialInvoiceStampOrSeal}
                                style={{ color: "red" }}
                              >
                                *{formData.commercialInvoiceStampOrSeal}
                              </option>
                            )}
                        </Form.Select>
                      </Col>
                    </Form.Group>
                  </Col>
                </Col>
              </Row>

              <Row className="international-shipping-comments mb-3">
                <Col className="mb-3">
                  <Form.Group controlId="internationalShipmentComments">
                    <Form.Label>
                      <OverlayTrigger
                        placement="top"
                        overlay={renderTooltip(
                          "InternationalShipmentComments"
                        )}
                        delay={{ show: 300, hide: 200 }}
                      >
                        <span>International Shipment Comments</span>
                      </OverlayTrigger>
                    </Form.Label>
                    <Col className="mb-3">
                      <Form.Control
                        as="textarea"
                        rows={4}
                        name="internationalShipmentComments"
                        value={formData.internationalShipmentComments}
                        onFocus={handleFocus}
                        onBlur={(e) =>
                          handleBlur(
                            e,
                            "International Shipment Comments",
                            [],
                            {
                              ID: formData.ID,
                              url: apiUrl,
                              field: "InternationalShipmentComments",
                              tableKey,
                            }
                          )
                        }
                        disabled={
                          isDisabled ||
                          (!roles.Admin && !roles.Apps && !roles.PSN)
                        }
                        className={fieldClasses.internationalShipmentComments}
                        onChange={handleChange}
                        placeholder="Enter comments"
                      />
                    </Col>
                  </Form.Group>
                </Col>
              </Row>
            </Form>
          </Row>
        </div>
      )}
    </div>
  );
}

export default SoldToShipTo;
